import React from 'react';
import { useDrag } from 'react-dnd';
import { SHAPE_CONFIGS } from './TableShape';
import { VENUE_ELEMENT_CONFIGS } from './VenueElementShape';
import { CUSTOM_PIECE_CONFIGS } from './CustomPieceShape';
import { Circle, Square, RectangleHorizontal, Music, Camera, DoorOpen, Wine, Bath, Puzzle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

// Custom Oval icon since lucide-react doesn't have one
const OvalIcon = ({ className }) => (
  <svg 
    className={className}
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <ellipse cx="12" cy="12" rx="10" ry="6" />
  </svg>
);

// Custom L-Shape icon
const LShapeIcon = ({ className }) => (
  <svg 
    className={className}
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M4 4 L12 4 L12 12 L20 12 L20 20 L4 20 Z" />
  </svg>
);

// Custom U-Shape icon
const UShapeIcon = ({ className }) => (
  <svg 
    className={className}
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M4 4 L8 4 L8 14 L16 14 L16 4 L20 4 L20 20 L4 20 Z" />
  </svg>
);

// Custom Serpentine 2 icon (2-arc wave)
const SShapeIcon = ({ className }) => (
  <svg 
    className={className}
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    {/* 2-arc wave - top */}
    <path d="M2,8 C6,2 10,2 12,8 C14,14 18,14 22,8" />
    {/* 2-arc wave - bottom */}
    <path d="M2,16 C6,22 10,22 12,16 C14,10 18,10 22,16" />
    {/* Connect ends */}
    <line x1="2" y1="8" x2="2" y2="16" />
    <line x1="22" y1="8" x2="22" y2="16" />
  </svg>
);

// Custom Crescent icon
const CrescentIcon = ({ className }) => (
  <svg 
    className={className}
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M4 18 Q4 6 12 6 Q20 6 20 18 Q16 12 12 12 Q8 12 4 18 Z" />
  </svg>
);

// Custom Serpentine icon (3-arc S-curve)
const SerpentineIcon = ({ className }) => (
  <svg 
    className={className}
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    {/* Top S-curve */}
    <path d="M2,8 C6,2 10,2 12,8 S18,14 22,8" />
    {/* Bottom S-curve */}
    <path d="M2,16 C6,22 10,22 12,16 S18,10 22,16" />
    {/* Connect ends */}
    <line x1="2" y1="8" x2="2" y2="16" />
    <line x1="22" y1="8" x2="22" y2="16" />
  </svg>
);

// Custom Serpentine4 icon (4-arc wave)
const Serpentine4Icon = ({ className }) => (
  <svg 
    className={className}
    viewBox="0 0 32 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    {/* 4-arc wave - top */}
    <path d="M2,8 C5,2 8,2 10,8 C12,14 15,14 17,8 C19,2 22,2 24,8 C26,14 29,14 30,8" />
    {/* 4-arc wave - bottom */}
    <path d="M2,16 C5,22 8,22 10,16 C12,10 15,10 17,16 C19,22 22,22 24,16 C26,10 29,10 30,16" />
    {/* Connect ends */}
    <line x1="2" y1="8" x2="2" y2="16" />
    <line x1="30" y1="8" x2="30" y2="16" />
  </svg>
);

// Custom Serpentine5 icon (5-arc wave)
const Serpentine5Icon = ({ className }) => (
  <svg 
    className={className}
    viewBox="0 0 40 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    {/* 5-arc wave - top */}
    <path d="M2,8 C4,2 7,2 9,8 C11,14 14,14 16,8 C18,2 21,2 23,8 C25,14 28,14 30,8 C32,2 35,2 38,8" />
    {/* 5-arc wave - bottom */}
    <path d="M2,16 C4,22 7,22 9,16 C11,10 14,10 16,16 C18,22 21,22 23,16 C25,10 28,10 30,16 C32,22 35,22 38,16" />
    {/* Connect ends */}
    <line x1="2" y1="8" x2="2" y2="16" />
    <line x1="38" y1="8" x2="38" y2="16" />
  </svg>
);

// Custom Serpentine6 icon (6-arc wave) - same size as Serpentine 5
const Serpentine6Icon = ({ className }) => (
  <svg 
    className={className}
    viewBox="0 0 40 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M2,8 C4,2 6,2 8,8 C10,14 12,14 14,8 C16,2 18,2 20,8 C22,14 24,14 26,8 C28,2 30,2 32,8 C34,14 36,14 38,8" />
    <path d="M2,16 C4,22 6,22 8,16 C10,10 12,10 14,16 C16,22 18,22 20,16 C22,10 24,10 26,16 C28,22 30,22 32,16 C34,10 36,10 38,16" />
    <line x1="2" y1="8" x2="2" y2="16" />
    <line x1="38" y1="8" x2="38" y2="16" />
  </svg>
);

// Custom Serpentine7 icon (7-arc wave) - same size as Serpentine 5
const Serpentine7Icon = ({ className }) => (
  <svg 
    className={className}
    viewBox="0 0 40 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M2,8 C3.5,2 5,2 6.5,8 C8,14 9.5,14 11,8 C12.5,2 14,2 15.5,8 C17,14 18.5,14 20,8 C21.5,2 23,2 24.5,8 C26,14 27.5,14 29,8 C30.5,2 32,2 33.5,8 C35,14 36.5,14 38,8" />
    <path d="M2,16 C3.5,22 5,22 6.5,16 C8,10 9.5,10 11,16 C12.5,22 14,22 15.5,16 C17,10 18.5,10 20,16 C21.5,22 23,22 24.5,16 C26,10 27.5,10 29,16 C30.5,22 32,22 33.5,16 C35,10 36.5,10 38,16" />
    <line x1="2" y1="8" x2="2" y2="16" />
    <line x1="38" y1="8" x2="38" y2="16" />
  </svg>
);

// Custom Half Round icon (2 arcs forming horseshoe/U-shape)
const HalfRoundIcon = ({ className }) => (
  <svg 
    className={className}
    viewBox="0 0 24 16" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    {/* Left arc - curves down from center */}
    <path d="M12,2 Q2,2 2,14" />
    {/* Right arc - curves down from center */}
    <path d="M12,2 Q22,2 22,14" />
  </svg>
);

// Custom Stage icon
const StageIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="8" width="20" height="8" rx="1" />
    <line x1="6" y1="16" x2="6" y2="20" />
    <line x1="18" y1="16" x2="18" y2="20" />
  </svg>
);

// Custom Dance Floor icon
const DanceFloorIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="1" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="12" y1="3" x2="12" y2="21" />
  </svg>
);

// Custom Bar icon
const BarIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="10" width="20" height="6" rx="1" />
    <circle cx="6" cy="13" r="1" fill="currentColor" />
    <circle cx="12" cy="13" r="1" fill="currentColor" />
    <circle cx="18" cy="13" r="1" fill="currentColor" />
  </svg>
);

// Custom Entrance icon
const EntranceIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="4" y="8" width="16" height="10" rx="1" />
    <path d="M12 18 L12 22" />
    <path d="M8 22 L16 22" />
    <path d="M12 4 L12 8" />
    <path d="M9 5 L12 2 L15 5" />
  </svg>
);

// Custom Cocktail Table icon
const CocktailIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const LIBRARY_SHAPES = [
  { id: 'round', icon: Circle, label: 'Round Table', defaultSeats: 12 },
  { id: 'square', icon: Square, label: 'Square Table', defaultSeats: 12 },
  { id: 'rectangle', icon: RectangleHorizontal, label: 'Long Table', defaultSeats: 12 },
  { id: 'oval', icon: OvalIcon, label: 'Oval Table', defaultSeats: 12 },
  { id: 'lshape', icon: LShapeIcon, label: 'L-Shape Table', defaultSeats: 14 },
  { id: 'ushape', icon: UShapeIcon, label: 'U-Shape Table', defaultSeats: 18 },
  { id: 'halfround', icon: HalfRoundIcon, label: 'Half Round', defaultSeats: 12 },
  { id: 'sshape', icon: SShapeIcon, label: 'Serpentine 2', defaultSeats: 12 },
  { id: 'serpentine3', icon: SerpentineIcon, label: 'Serpentine 3', defaultSeats: 18 },
  { id: 'serpentine4', icon: Serpentine4Icon, label: 'Serpentine 4', defaultSeats: 24 },
  { id: 'serpentine5', icon: Serpentine5Icon, label: 'Serpentine 5', defaultSeats: 30 },
  { id: 'serpentine6', icon: Serpentine6Icon, label: 'Serpentine 6', defaultSeats: 36 },
  { id: 'serpentine7', icon: Serpentine7Icon, label: 'Serpentine 7', defaultSeats: 42 },
];

// Venue Elements Library
const VENUE_ELEMENTS = [
  { id: 'stage', icon: StageIcon, label: 'Stage' },
  { id: 'dance_floor', icon: DanceFloorIcon, label: 'Dance Floor' },
  { id: 'bar', icon: BarIcon, label: 'Bar' },
  { id: 'dj_booth', icon: Music, label: 'DJ Booth' },
  { id: 'photo_booth', icon: Camera, label: 'Photo Booth' },
  { id: 'entrance', icon: EntranceIcon, label: 'Entrance' },
  { id: 'cocktail_table', icon: CocktailIcon, label: 'Cocktail' },
  { id: 'restrooms', icon: Bath, label: 'Restrooms' },
];

// Custom Piece Icons
const ArcIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M 4 16 Q 12 4 20 16" />
  </svg>
);

// Seat icon
const SeatIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="12" r="6" />
  </svg>
);

// Custom Pieces Library (for modular table building)
const CUSTOM_PIECES = [
  { id: 'piece_rectangle', icon: RectangleHorizontal, label: 'Rectangle', defaultSeats: 0 },
  { id: 'piece_square', icon: Square, label: 'Square', defaultSeats: 0 },
  { id: 'piece_circle', icon: Circle, label: 'Circle', defaultSeats: 0 },
  { id: 'piece_wave', icon: ArcIcon, label: 'Arc', defaultSeats: 0 },
];

function DraggableShape({ shape, onAddTable, adminDarkMode }) {
  const config = SHAPE_CONFIGS[shape.id];
  
  const [{ isDragging }, drag] = useDrag({
    type: 'TABLE_SHAPE',
    item: () => ({
      type: 'TABLE_SHAPE',
      shape: shape.id,
      defaultSeats: shape.defaultSeats,
    }),
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult();
      if (dropResult?.isNew && dropResult.x !== undefined) {
        onAddTable({
          shape: shape.id,
          seats: shape.defaultSeats,
          position_x: dropResult.x,
          position_y: dropResult.y,
        });
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const Icon = shape.icon;
  
  // Use fixed preview size for serpentine tables (3-7) to match serpentine 2
  const isSerpentine = shape.id.startsWith('serpentine') || shape.id === 'sshape';
  const previewWidth = isSerpentine ? 80 : Math.min(config.width * 0.5, 300);
  const previewHeight = isSerpentine ? 40 : Math.min(config.height * 0.5, 60);

  return (
    <div
      ref={drag}
      className={`
        flex flex-col items-center gap-2 p-3 rounded-lg
        cursor-grab active:cursor-grabbing
        transition-all duration-200
        ${adminDarkMode 
          ? 'bg-slate-800/50 border border-slate-700 hover:border-[#D4AF37] hover:bg-slate-800' 
          : 'bg-slate-50 border border-slate-200 hover:border-[var(--admin-accent)] hover:bg-slate-100'}
        ${isDragging ? 'opacity-50 scale-95' : 'opacity-100'}
      `}
      data-testid={`library-shape-${shape.id}`}
    >
      <div
        className={`flex items-center justify-center border ${adminDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-slate-300'}`}
        style={{
          width: previewWidth,
          height: previewHeight,
          borderRadius: config.borderRadius,
        }}
      >
        <Icon className={`h-5 w-5 ${adminDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
      </div>
      <span className={`text-xs text-center ${adminDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{shape.label}</span>
      <span className={`text-[10px] ${adminDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{shape.defaultSeats} seats</span>
    </div>
  );
}

// Draggable Venue Element for library
function DraggableVenueElement({ element, onAddElement, adminDarkMode }) {
  const config = VENUE_ELEMENT_CONFIGS[element.id];
  
  const [{ isDragging }, drag] = useDrag({
    type: 'VENUE_ELEMENT_NEW',
    item: () => ({
      type: 'VENUE_ELEMENT_NEW',
      element_type: element.id,
      width: config.width,
      height: config.height,
      label: config.label,
    }),
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult();
      if (dropResult?.isNew && dropResult.x !== undefined) {
        onAddElement({
          element_type: element.id,
          label: config.label,
          position_x: dropResult.x,
          position_y: dropResult.y,
          width: config.width,
          height: config.height,
        });
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const Icon = element.icon;
  const isCircle = config.isCircle;

  return (
    <div
      ref={drag}
      className={`
        flex flex-col items-center gap-2 p-3 rounded-lg
        cursor-grab active:cursor-grabbing
        transition-all duration-200
        ${adminDarkMode 
          ? 'bg-slate-800/50 border border-slate-700 hover:border-[#D4AF37] hover:bg-slate-800' 
          : 'bg-slate-50 border border-slate-200 hover:border-[var(--admin-accent)] hover:bg-slate-100'}
        ${isDragging ? 'opacity-50 scale-95' : 'opacity-100'}
      `}
      data-testid={`library-element-${element.id}`}
    >
      <div
        className={`flex items-center justify-center border-2 bg-transparent ${adminDarkMode ? 'border-slate-500' : 'border-slate-300'}`}
        style={{
          width: Math.min(config.width * 0.4, 50),
          height: Math.min(config.height * 0.4, 40),
          borderRadius: isCircle ? '50%' : '2px',
        }}
      >
        <Icon className={`h-4 w-4 ${adminDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
      </div>
      <span className={`text-xs text-center ${adminDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{element.label}</span>
    </div>
  );
}

// Draggable Custom Piece for library (modular table building)
function DraggableCustomPiece({ piece, onAddPiece, adminDarkMode }) {
  const config = CUSTOM_PIECE_CONFIGS[piece.id];
  
  const [{ isDragging }, drag] = useDrag({
    type: 'CUSTOM_PIECE_NEW',
    item: () => ({
      type: 'CUSTOM_PIECE_NEW',
      piece_type: piece.id,
      width: config.width,
      height: config.height,
      label: config.label,
      seats: config.defaultSeats,
      fill_color: '#3b82f6',
      stroke_color: '#1e40af',
    }),
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult();
      if (dropResult?.isNew && dropResult.x !== undefined) {
        onAddPiece({
          piece_type: piece.id,
          label: config.label,
          position_x: dropResult.x,
          position_y: dropResult.y,
          width: config.width,
          height: config.height,
          seats: config.defaultSeats,
          fill_color: '#3b82f6',
          stroke_color: '#1e40af',
        });
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const Icon = piece.icon;
  const isCircle = config.isCircle;
  const isArc = config.isArc;

  return (
    <div
      ref={drag}
      className={`
        flex flex-col items-center gap-2 p-3 rounded-lg
        cursor-grab active:cursor-grabbing
        transition-all duration-200
        ${adminDarkMode 
          ? 'bg-slate-800/50 border border-blue-900/50 hover:border-blue-500 hover:bg-slate-800' 
          : 'bg-blue-50 border border-blue-200 hover:border-blue-400 hover:bg-blue-100'}
        ${isDragging ? 'opacity-50 scale-95' : 'opacity-100'}
      `}
      data-testid={`library-piece-${piece.id}`}
    >
      <div
        className="flex items-center justify-center border-2 border-blue-500/50 bg-blue-500/20"
        style={{
          width: Math.min(config.width * 0.5, 50),
          height: Math.min(config.height * 0.5, 40),
          borderRadius: isCircle ? '50%' : (isArc ? '50% 50% 0 0' : '4px'),
        }}
      >
        <Icon className="h-4 w-4 text-blue-400" />
      </div>
      <span className={`text-xs text-center ${adminDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{piece.label}</span>
    </div>
  );
}

// Draggable Seat for adding to custom pieces
function DraggableSeat({ onAddPiece, adminDarkMode }) {
  const [{ isDragging }, drag] = useDrag({
    type: 'CUSTOM_PIECE_NEW',
    item: () => ({
      type: 'CUSTOM_PIECE_NEW',
      piece_type: 'piece_seat',
      width: 30,
      height: 30,
    }),
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult();
      if (dropResult?.isNew && dropResult.x !== undefined && onAddPiece) {
        onAddPiece({
          piece_type: 'piece_seat',
          label: 'Seat',
          position_x: dropResult.x,
          position_y: dropResult.y,
          width: 30,
          height: 30,
          seats: 1,
          fill_color: '#f59e0b', // Amber color for seats
          stroke_color: '#d97706',
        });
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <div
      ref={drag}
      className={`
        flex flex-col items-center gap-2 p-3 rounded-lg
        cursor-grab active:cursor-grabbing
        transition-all duration-200
        col-span-2
        ${adminDarkMode 
          ? 'bg-slate-800/50 border border-amber-900/50 hover:border-amber-500 hover:bg-slate-800' 
          : 'bg-amber-50 border border-amber-200 hover:border-amber-400 hover:bg-amber-100'}
        ${isDragging ? 'opacity-50 scale-95' : 'opacity-100'}
      `}
      data-testid="library-seat"
    >
      <div className="flex items-center gap-3">
        <div
          className="flex items-center justify-center bg-amber-500/30 border-2 border-amber-500/50 rounded-full"
          style={{ width: 24, height: 24 }}
        >
          <SeatIcon className="h-3 w-3 text-amber-400" />
        </div>
        <div className="text-left">
          <span className={`text-xs block ${adminDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Seat</span>
          <span className="text-[10px] text-amber-400/70">Drag onto canvas</span>
        </div>
      </div>
    </div>
  );
}

export default function TableLibrary({ tables, onAddTable, onAddVenueElement, onAddCustomPiece }) {
  const { adminDarkMode } = useTheme();
  // Tables not yet placed on canvas
  const unplacedTables = tables.filter(t => t.position_x === null || t.position_y === null);

  return (
    <div className="flex flex-col gap-4 h-full overflow-y-auto">
      {/* Table Shapes Library */}
      <div className={`p-4 rounded-lg border ${adminDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-slate-200 shadow-sm'}`}>
        <h3 className={`text-sm font-semibold mb-3 ${adminDarkMode ? 'text-white' : 'text-slate-800'}`}>Table Shapes</h3>
        <p className={`text-xs mb-3 ${adminDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Drag shapes onto the canvas</p>
        <div className="grid grid-cols-2 gap-2">
          {LIBRARY_SHAPES.map(shape => (
            <DraggableShape
              key={shape.id}
              shape={shape}
              onAddTable={onAddTable}
              adminDarkMode={adminDarkMode}
            />
          ))}
        </div>
      </div>
      
      {/* Custom Pieces Library (Modular Table Building) */}
      {onAddCustomPiece && (
        <div className={`p-4 rounded-lg border ${adminDarkMode ? 'bg-slate-900/50 border-blue-900/30' : 'bg-blue-50 border-blue-200 shadow-sm'}`}>
          <h3 className={`text-sm font-semibold mb-1 flex items-center gap-2 ${adminDarkMode ? 'text-white' : 'text-slate-800'}`}>
            <Puzzle className="h-4 w-4 text-blue-400" />
            Custom Pieces
          </h3>
          <p className={`text-xs mb-3 ${adminDarkMode ? 'text-blue-400/70' : 'text-blue-600/70'}`}>Build custom table shapes</p>
          <div className="grid grid-cols-2 gap-2">
            {CUSTOM_PIECES.map(piece => (
              <DraggableCustomPiece
                key={piece.id}
                piece={piece}
                onAddPiece={onAddCustomPiece}
                adminDarkMode={adminDarkMode}
              />
            ))}
            <DraggableSeat onAddPiece={onAddCustomPiece} adminDarkMode={adminDarkMode} />
          </div>
          <p className={`text-xs mt-3 leading-relaxed ${adminDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            1. Drag shapes to canvas and resize<br/>
            2. Select piece, then use +/- to add seats<br/>
            3. Drag seats to position them<br/>
            4. Shift+Click multiple pieces to group
          </p>
        </div>
      )}
      
      {/* Venue Elements Library */}
      {onAddVenueElement && (
        <div className={`p-4 rounded-lg border ${adminDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-slate-200 shadow-sm'}`}>
          <h3 className={`text-sm font-semibold mb-3 ${adminDarkMode ? 'text-white' : 'text-slate-800'}`}>Venue Elements</h3>
          <p className={`text-xs mb-3 ${adminDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Drag elements onto the canvas</p>
          <div className="grid grid-cols-2 gap-2">
            {VENUE_ELEMENTS.map(element => (
              <DraggableVenueElement
                key={element.id}
                element={element}
                onAddElement={onAddVenueElement}
                adminDarkMode={adminDarkMode}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Unplaced Tables */}
      {unplacedTables.length > 0 && (
        <div className={`p-4 rounded-lg border flex-1 overflow-auto ${adminDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-slate-200 shadow-sm'}`}>
          <h3 className={`text-sm font-semibold mb-3 ${adminDarkMode ? 'text-white' : 'text-slate-800'}`}>
            Unplaced Tables
            <span className={`ml-2 text-xs font-normal ${adminDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
              ({unplacedTables.length})
            </span>
          </h3>
          <p className={`text-xs mb-3 ${adminDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            Drag these existing tables to the canvas
          </p>
          <div className="space-y-2">
            {unplacedTables.map(table => (
              <UnplacedTableItem key={table.id} table={table} adminDarkMode={adminDarkMode} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UnplacedTableItem({ table, adminDarkMode }) {
  const [{ isDragging }, drag] = useDrag({
    type: 'TABLE',
    item: () => ({
      id: table.id,
      type: 'TABLE',
      shape: table.shape || 'round',
      table_name: table.table_name,
      seats: table.seats || table.capacity || 8,
    }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <div
      ref={drag}
      className={`
        flex items-center justify-between p-2.5 rounded-lg
        cursor-grab active:cursor-grabbing
        transition-all duration-200
        ${adminDarkMode 
          ? 'bg-slate-800/30 border border-slate-700 hover:border-[#D4AF37] hover:bg-slate-800/50' 
          : 'bg-slate-50 border border-slate-200 hover:border-[var(--admin-accent)] hover:bg-slate-100'}
        ${isDragging ? 'opacity-50' : 'opacity-100'}
      `}
      data-testid={`unplaced-table-${table.id}`}
    >
      <div className="flex items-center gap-2">
        <div 
          className={`w-6 h-6 flex items-center justify-center ${adminDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}
          style={{
            borderRadius: table.shape === 'rectangle' ? '4px' : 
                         table.shape === 'square' ? '4px' : '50%'
          }}
        >
          <span className={`text-[8px] ${adminDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {(table.shape || 'R')[0].toUpperCase()}
          </span>
        </div>
        <span className={`text-sm truncate max-w-[100px] ${adminDarkMode ? 'text-white' : 'text-slate-800'}`}>
          {table.table_name}
        </span>
      </div>
      <span className={`text-xs ${adminDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
        {table.capacity || table.seats || 0} seats
      </span>
    </div>
  );
}
